import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthProvider';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { 
  listUsers, 
  getUser, 
  updateUserRole, 
  removeUserFromTenant,
  listTenants
} from '../../services/platformAdminService';

const {
  FiUsers,
  FiUser,
  FiSearch,
  FiRefreshCw,
  FiX,
  FiAlertTriangle,
  FiEye,
  FiEdit2,
  FiUserMinus,
  FiShield,
  FiMail,
  FiClock,
  FiHome,
  FiFilter
} = FiIcons;

/**
 * UserManagement - CRUD interface for managing users across tenants
 * Includes 2-level confirmation for role changes and removal
 */
const UserManagement = () => {
  const { hasSuperadminPermission } = useAuth();
  
  // State
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenantFilter, setSelectedTenantFilter] = useState('');
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Form state
  const [newRole, setNewRole] = useState('');
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Remove confirmation state
  const [removeStep, setRemoveStep] = useState(1);
  const [removeConfirmText, setRemoveConfirmText] = useState('');

  const canWrite = hasSuperadminPermission('users:write');

  // Fetch users and tenants on mount
  useEffect(() => {
    fetchTenants();
    fetchUsers();
  }, []);

  const fetchTenants = async () => {
    try {
      const data = await listTenants({});
      setTenants(data.tenants || []);
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  };

  const fetchUsers = async (search = '', tenantId = '') => {
    setLoading(true);
    setError(null);
    try {
      const data = await listUsers({ 
        search, 
        tenantId: tenantId || null 
      });
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchQuery, selectedTenantFilter);
  };

  const handleTenantFilter = (tenantId) => {
    setSelectedTenantFilter(tenantId);
    fetchUsers(searchQuery, tenantId);
  };

  // View user details
  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
    setDetailsLoading(true);
    try {
      const data = await getUser(user.id);
      setUserDetails(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Change role
  const openRoleModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.tenant_role || 'user');
    setFormError(null);
    setShowRoleModal(true);
  };

  const handleRoleChange = async () => {
    if (!newRole || newRole === selectedUser.tenant_role) {
      setFormError('Please select a different role');
      return;
    }
    
    setFormLoading(true);
    setFormError(null);
    try {
      await updateUserRole(selectedUser.id, newRole);
      setShowRoleModal(false);
      setSelectedUser(null);
      fetchUsers(searchQuery, selectedTenantFilter);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Remove user
  const openRemoveModal = (user) => {
    setSelectedUser(user);
    setRemoveStep(1);
    setRemoveConfirmText('');
    setFormError(null);
    setShowRemoveModal(true);
  };

  const handleRemoveStep1 = () => {
    setRemoveStep(2);
  };

  const handleRemoveConfirm = async () => {
    if (removeConfirmText !== selectedUser.email) {
      setFormError('Confirmation text does not match user email');
      return;
    }
    
    setFormLoading(true);
    setFormError(null);
    try {
      await removeUserFromTenant(selectedUser.id, removeConfirmText);
      setShowRemoveModal(false);
      setSelectedUser(null);
      setRemoveStep(1);
      setRemoveConfirmText('');
      fetchUsers(searchQuery, selectedTenantFilter);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const closeModals = () => {
    setShowDetailsModal(false);
    setShowRoleModal(false);
    setShowRemoveModal(false);
    setSelectedUser(null);
    setUserDetails(null);
    setFormError(null);
    setRemoveStep(1);
    setRemoveConfirmText('');
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  // Get activity status
  const getActivityStatus = (lastSignIn) => {
    if (!lastSignIn) return { label: 'Never', color: 'gray' };
    const diff = Date.now() - new Date(lastSignIn).getTime();
    const hours = diff / (1000 * 60 * 60);
    if (hours < 1) return { label: 'Active', color: 'green' };
    if (hours < 24) return { label: 'Today', color: 'blue' };
    if (hours < 168) return { label: 'This week', color: 'yellow' };
    return { label: 'Inactive', color: 'gray' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <p className="text-sm text-gray-500 mt-1">Manage users across all tenants</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex space-x-2 flex-1">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Search
          </button>
        </form>

        {/* Tenant Filter */}
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-400" />
          <select
            value={selectedTenantFilter}
            onChange={(e) => handleTenantFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Tenants</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
            ))}
          </select>
        </div>

        {/* Refresh */}
        <button
          onClick={() => fetchUsers(searchQuery, selectedTenantFilter)}
          className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No users found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Tenant</th>
                <th className="px-4 py-3 text-center">Role</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-left">Last Sign In</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => {
                const activity = getActivityStatus(user.last_sign_in_at);
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-indigo-600">
                            {user.email?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.email}</p>
                          {user.is_superadmin && (
                            <span className="inline-flex items-center text-xs text-purple-600">
                              <SafeIcon icon={FiShield} className="w-3 h-3 mr-1" />
                              {user.superadmin_role}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {user.tenant ? (
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiHome} className="w-3 h-3 text-gray-400" />
                          <span className="text-sm">{user.tenant.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No tenant</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {user.tenant_role ? (
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          user.tenant_role === 'owner' ? 'bg-purple-100 text-purple-700' :
                          user.tenant_role === 'admin' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {user.tenant_role}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        activity.color === 'green' ? 'bg-green-100 text-green-700' :
                        activity.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                        activity.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {activity.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(user.last_sign_in_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewDetails(user)}
                          className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                          title="View Details"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </button>
                        {canWrite && user.tenant && (
                          <>
                            <button
                              onClick={() => openRoleModal(user)}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Change Role"
                            >
                              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openRemoveModal(user)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Remove from Tenant"
                            >
                              <SafeIcon icon={FiUserMinus} className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">User Details</h3>
              <button onClick={closeModals} className="p-1 hover:bg-gray-100 rounded">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {detailsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                </div>
              ) : userDetails ? (
                <div className="space-y-4">
                  {/* User Info */}
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl font-medium text-indigo-600">
                        {userDetails.email?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{userDetails.email}</p>
                      {userDetails.is_superadmin && (
                        <span className="inline-flex items-center text-sm text-purple-600">
                          <SafeIcon icon={FiShield} className="w-4 h-4 mr-1" />
                          {userDetails.superadmin_role}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <p className="text-xs text-gray-500 flex items-center">
                        <SafeIcon icon={FiHome} className="w-3 h-3 mr-1" />
                        Tenant
                      </p>
                      <p className="font-medium">{userDetails.tenant?.name || 'None'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Role</p>
                      <p className="font-medium">{userDetails.tenant_role || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 flex items-center">
                        <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                        Created
                      </p>
                      <p className="font-medium text-sm">{formatDate(userDetails.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Last Sign In</p>
                      <p className="font-medium text-sm">{formatDate(userDetails.last_sign_in_at)}</p>
                    </div>
                    {userDetails.stats && (
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500">Cases Created</p>
                        <p className="font-medium">{userDetails.stats.cases_created}</p>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t">
                    <p className="text-xs text-gray-500">User ID</p>
                    <p className="font-mono text-xs text-gray-600">{userDetails.id}</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Failed to load details</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal (2-level confirmation) */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Change User Role</h3>
              <button onClick={closeModals} className="p-1 hover:bg-gray-100 rounded">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {formError}
                </div>
              )}
              
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Changing role for: <strong>{selectedUser.email}</strong>
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Current role: <span className="font-medium">{selectedUser.tenant_role}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Warning:</strong> Changing a user's role will affect their permissions immediately.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={closeModals}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRoleChange}
                  disabled={formLoading || newRole === selectedUser.tenant_role}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {formLoading ? 'Updating...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Modal (2-level confirmation) */}
      {showRemoveModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-red-900">Remove User</h3>
              </div>
              <button onClick={closeModals} className="p-1 hover:bg-red-100 rounded">
                <SafeIcon icon={FiX} className="w-5 h-5 text-red-600" />
              </button>
            </div>
            <div className="p-6">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-4">
                  {formError}
                </div>
              )}
              
              {removeStep === 1 ? (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to remove <strong>{selectedUser.email}</strong> from 
                    <strong> {selectedUser.tenant?.name}</strong>?
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>Warning:</strong> This will revoke the user's access to this tenant.
                      They will no longer be able to view or manage any data.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={closeModals}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRemoveStep1}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Yes, Continue
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-700 mb-4">
                    To confirm removal, type the user's email: <strong>{selectedUser.email}</strong>
                  </p>
                  <input
                    type="text"
                    value={removeConfirmText}
                    onChange={(e) => setRemoveConfirmText(e.target.value)}
                    placeholder="Type email to confirm"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 mb-4"
                  />
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setRemoveStep(1)}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleRemoveConfirm}
                      disabled={formLoading || removeConfirmText !== selectedUser.email}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {formLoading ? 'Removing...' : 'Confirm Remove'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
